import * as React from "react";
import { Menu } from "@base-ui/react/menu";
import { CheckIcon, CaretRightIcon, CircleIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";

/**
 * Migrated from `@radix-ui/react-dropdown-menu` to Base UI.
 *
 * The public API of every export here is unchanged, deliberately: 53 files
 * consume this component and the migration is worth nothing if it also has to
 * be a 53-file rewrite. Three Base UI differences are therefore absorbed inside
 * these wrappers rather than pushed onto callers.
 *
 *  1. **`asChild` → `render`.** 58 call sites pass `asChild` (50 of them
 *     wrapping a `<Button>`). `DropdownMenuTrigger` translates it below, so
 *     they keep working untouched. This is an adapter, not drift: the two props
 *     mean the same thing, and a mechanical sweep to `render` can follow as its
 *     own PR without blocking the primitive swap.
 *  2. **`Content` → `Positioner` + `Popup`.** Base UI splits the single Radix
 *     part in two and moves positioning onto the outer one. `align`, `side` and
 *     `sideOffset` — passed by ~59 call sites — are routed to `Positioner`
 *     here, so the caller still just sets them on `DropdownMenuContent`.
 *  3. **`onSelect` → `onClick`.** `DropdownMenuItem` accepts the Radix name and
 *     forwards it, so the 9 call sites using it are unaffected.
 *
 * Two things could NOT be absorbed and are real changes:
 *
 *  - Base UI's menu has no `Separator` part at all, so `DropdownMenuSeparator`
 *    now renders its own `role="separator"` element. Same markup contract, same
 *    classes; it is simply no longer a primitive.
 *  - Radix let an `onSelect` handler call `preventDefault()` to keep the menu
 *    open. Base UI expresses that as `closeOnClick={false}` instead, and there
 *    is no faithful way to shim it because the decision is needed before the
 *    handler runs. Exactly one call site relied on it
 *    (`data-table-view-options.tsx`) and is updated in the same commit.
 *
 * State attributes and CSS variables are renamed throughout: `data-[state=open]`
 * → `data-open`, `data-[state=closed]` → `data-closed`, and the
 * `--radix-dropdown-menu-*` custom properties → Base UI's `--transform-origin`,
 * `--available-height` and `--anchor-width`. These are invisible to callers but
 * they are what actually drive the open animation and the max-height, so the
 * screenshot and animation baselines are what prove they were all caught.
 */

/** Shared popup surface classes, used by both the menu and its submenus. */
const popupClassName =
  "bg-popover text-popover-foreground data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--transform-origin) rounded-md border p-1 shadow-md outline-hidden";

function DropdownMenu({ ...props }: Menu.Root.Props) {
  return <Menu.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuPortal({ ...props }: Menu.Portal.Props) {
  return <Menu.Portal data-slot="dropdown-menu-portal" {...props} />;
}

/**
 * Accepts Radix's `asChild` and maps it to Base UI's `render`.
 *
 * `asChild` renders the single child element *as* the trigger, merging props
 * into it; `render` takes that element directly. So the translation is to hand
 * the child over as `render` — which is why the 58 existing call sites need no
 * edit. Passing `render` yourself still works and takes precedence.
 */
function DropdownMenuTrigger({
  asChild,
  render,
  children,
  ...props
}: Omit<Menu.Trigger.Props, "className"> & {
  className?: string;
  asChild?: boolean;
}) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <Menu.Trigger
      data-slot="dropdown-menu-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </Menu.Trigger>
  );
}

/**
 * Radix's `Content` in one wrapper: portal, positioning and surface.
 *
 * Callers pass `align`/`side`/`sideOffset` here as they always did; they are
 * split off and handed to `Positioner`, while everything else lands on `Popup`.
 *
 * `anchor` is Base UI's own prop, forwarded for the same reason: a menu opened
 * from something other than a `DropdownMenuTrigger` (a row's context menu, say)
 * has nothing for the positioner to anchor to and never appears. Such a caller
 * passes a virtual anchor — `{ getBoundingClientRect }` at the cursor — instead
 * of positioning the popup itself, which under Radix used to work and here
 * would only fight the positioner.
 */
function DropdownMenuContent({
  className,
  sideOffset = 4,
  side,
  align,
  alignOffset,
  collisionPadding,
  anchor,
  ...props
}: Omit<Menu.Popup.Props, "className"> & {
  className?: string;
  sideOffset?: Menu.Positioner.Props["sideOffset"];
  side?: Menu.Positioner.Props["side"];
  align?: Menu.Positioner.Props["align"];
  alignOffset?: Menu.Positioner.Props["alignOffset"];
  collisionPadding?: Menu.Positioner.Props["collisionPadding"];
  anchor?: Menu.Positioner.Props["anchor"];
}) {
  return (
    <Menu.Portal>
      <Menu.Positioner
        sideOffset={sideOffset}
        side={side}
        align={align}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        anchor={anchor}
        className="z-[1500]" // TODO: remove this when MUI is removed
      >
        <Menu.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            popupClassName,
            "max-h-(--available-height) overflow-x-hidden overflow-y-auto",
            className,
          )}
          {...props}
        />
      </Menu.Positioner>
    </Menu.Portal>
  );
}

function DropdownMenuGroup({ ...props }: Menu.Group.Props) {
  return <Menu.Group data-slot="dropdown-menu-group" {...props} />;
}

/**
 * Accepts Radix's `onSelect` alongside Base UI's `onClick`.
 *
 * Base UI dropped the separate select event — an item is a button and clicking
 * it is selecting it — so `onSelect` is forwarded as `onClick`. Note this is
 * only faithful for handlers that *act*: a Radix handler calling
 * `preventDefault()` to hold the menu open has no equivalent here and needs
 * `closeOnClick={false}` instead.
 */
function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  onSelect,
  onClick,
  asChild,
  render,
  children,
  ...props
}: Omit<Menu.Item.Props, "className"> & {
  className?: string;
  inset?: boolean;
  variant?: "default" | "destructive";
  onSelect?: Menu.Item.Props["onClick"];
  asChild?: boolean;
}) {
  // Items take `asChild` too — 5 call sites wrap a link or a button in one.
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <Menu.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      onClick={onClick ?? onSelect}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </Menu.Item>
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: Omit<Menu.CheckboxItem.Props, "className"> & { className?: string }) {
  return (
    <Menu.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <Menu.CheckboxItemIndicator>
          <CheckIcon className="size-4" />
        </Menu.CheckboxItemIndicator>
      </span>
      {children}
    </Menu.CheckboxItem>
  );
}

function DropdownMenuRadioGroup({ ...props }: Menu.RadioGroup.Props) {
  return <Menu.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: Omit<Menu.RadioItem.Props, "className"> & { className?: string }) {
  return (
    <Menu.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <Menu.RadioItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </Menu.RadioItemIndicator>
      </span>
      {children}
    </Menu.RadioItem>
  );
}

/**
 * A plain element, like the separator, and for a similar reason.
 *
 * Base UI's nearest part is `Menu.GroupLabel`, but it *throws* unless it is
 * inside a `Menu.Group` ("MenuGroupContext is missing"). Radix's `Label` had no
 * such requirement and most of the 11 call sites here use it standalone, above
 * a separator rather than wrapping a group — so adopting `GroupLabel` would
 * mean restructuring their markup, and forgetting one would be a runtime crash
 * rather than a style regression.
 *
 * Nothing is lost by not using it: Radix's `Label` is a styled `div` that does
 * no ARIA wiring of its own, so this renders exactly what it did before. Groups
 * that *do* want a labelled region can still nest `DropdownMenuGroup`.
 */
function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<"div"> & {
  inset?: boolean;
}) {
  return (
    <div
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A plain element, not a primitive: Base UI's menu has no `Separator` part.
 *
 * The rendered markup and classes are unchanged, and `role="separator"` keeps
 * it in the accessibility tree exactly where Radix put it.
 */
function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      data-slot="dropdown-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}

function DropdownMenuSub({ ...props }: Menu.SubmenuRoot.Props) {
  return <Menu.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: Omit<Menu.SubmenuTrigger.Props, "className"> & {
  className?: string;
  inset?: boolean;
}) {
  return (
    <Menu.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8",
        className,
      )}
      {...props}
    >
      {children}
      <CaretRightIcon className="ml-auto size-4" />
    </Menu.SubmenuTrigger>
  );
}

/** Like `DropdownMenuContent`, a submenu needs its own portal + positioner. */
function DropdownMenuSubContent({
  className,
  sideOffset = 0,
  side = "inline-end",
  align = "start",
  ...props
}: Omit<Menu.Popup.Props, "className"> & {
  className?: string;
  sideOffset?: Menu.Positioner.Props["sideOffset"];
  side?: Menu.Positioner.Props["side"];
  align?: Menu.Positioner.Props["align"];
}) {
  return (
    <Menu.Portal>
      <Menu.Positioner
        sideOffset={sideOffset}
        side={side}
        align={align}
        className="z-[1500]" // TODO: remove this when MUI is removed
      >
        <Menu.Popup
          data-slot="dropdown-menu-sub-content"
          className={cn(popupClassName, "overflow-hidden shadow-lg", className)}
          {...props}
        />
      </Menu.Positioner>
    </Menu.Portal>
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
